/**
 * Converts <br>-separated content inside top-level <p>s into multiple <p> blocks
 * so paragraph-style spacing renders consistently regardless of the underlying
 * HTML shape. Schedule rich text lands in storage in three common shapes:
 *
 *   - <p>X</p><p>Y</p>           (typical Jodit Enter output)
 *   - <p>X<br>Y<br>Z</p>          (single <p> with <br> separators inside, from
 *                                  soft line breaks, legacy-imported content,
 *                                  or older save pipelines that collapsed <br><br>)
 *   - X<br>Y<br>Z                 (bare inline content)
 *
 * After this transform all three shapes render as <p>X</p><p>Y</p><p>Z</p>, so
 * margin-driven paragraph spacing is consistent in every view. Treats every <br>
 * inside a top-level <p> as a paragraph break: soft breaks are a non-goal in
 * schedule body text.
 *
 * Block elements (lists, tables, headings) are passed through unchanged.
 */

const BLOCK_TAGS = new Set([
    "P",
    "DIV",
    "OL",
    "UL",
    "TABLE",
    "BLOCKQUOTE",
    "H1",
    "H2",
    "H3",
    "H4",
    "H5",
    "H6",
    "PRE",
    "HR",
])

export function splitBreakSeparatedParagraphs(html) {
    if (typeof html !== "string" || !html.includes("<")) return html

    const container = document.createElement("div")
    container.innerHTML = html

    splitTopLevelParagraphsOnBreaks()
    wrapTopLevelInlineRunsInParagraphs()

    return container.innerHTML

    function splitTopLevelParagraphsOnBreaks() {
        const paragraphs = [...container.querySelectorAll(":scope > p")]
        for (const paragraph of paragraphs) {
            const chunks = splitOnBreaks([...paragraph.childNodes])
            if (chunks.length <= 1) continue

            const fragment = document.createDocumentFragment()
            for (const chunk of chunks) {
                const newParagraph = document.createElement("p")
                for (const attribute of paragraph.attributes) {
                    newParagraph.setAttribute(attribute.name, attribute.value)
                }
                for (const node of chunk) newParagraph.appendChild(node)
                fragment.appendChild(newParagraph)
            }
            paragraph.replaceWith(fragment)
        }
    }

    function wrapTopLevelInlineRunsInParagraphs() {
        const childNodes = [...container.childNodes]
        const groups = []
        let inlineRun = []

        for (const node of childNodes) {
            if (isBlockElement(node)) {
                if (inlineRun.length) groups.push({ type: "inline", nodes: inlineRun })
                groups.push({ type: "block", nodes: [node] })
                inlineRun = []
                continue
            }
            inlineRun.push(node)
        }
        if (inlineRun.length) groups.push({ type: "inline", nodes: inlineRun })

        const fragment = document.createDocumentFragment()
        for (const group of groups) {
            if (group.type === "block") {
                for (const node of group.nodes) fragment.appendChild(node)
                continue
            }
            const chunks = splitOnBreaks(group.nodes)
            for (const chunk of chunks) {
                if (!chunk.some((n) => !isIgnorableTextNode(n))) continue
                const newParagraph = document.createElement("p")
                for (const node of chunk) newParagraph.appendChild(node)
                fragment.appendChild(newParagraph)
            }
        }

        container.innerHTML = ""
        container.appendChild(fragment)
    }

    function splitOnBreaks(nodes) {
        const chunks = [[]]
        for (const node of nodes) {
            if (isBreakElement(node)) {
                if (chunks[chunks.length - 1].length > 0) chunks.push([])
                continue
            }
            if (isIgnorableTextNode(node)) continue
            chunks[chunks.length - 1].push(node)
        }
        return chunks.filter((chunk) => chunk.length > 0)
    }

    function isBlockElement(node) {
        return node?.nodeType === 1 && BLOCK_TAGS.has(node.tagName)
    }

    function isBreakElement(node) {
        return node?.nodeType === 1 && node.tagName === "BR"
    }

    function isIgnorableTextNode(node) {
        return node?.nodeType === 3 && !node.textContent?.trim()
    }
}
